import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getInitials } from './logo';

interface OnelinerTextProps {
  variant: '25' | '26';
  // The 25 page prints the full oneliner and truncates it at 100 characters; the 26 page
  // prints the short one and offers the long one in the dialog.
  text: string;
  fullText: string | null;
  projectName: string;
  projectLogoUrl: string | null;
  projectTrackName: string | null;
  isDefaultLogo: boolean;
  maxLength?: number;
}

export function OnelinerText({
  variant,
  text,
  fullText,
  projectName,
  projectLogoUrl,
  projectTrackName,
  isDefaultLogo: isDefaultLogoValue,
  maxLength = 100,
}: OnelinerTextProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const font = variant === '25' ? 'font-title' : 'font-sans';

  const { displayText, needsTruncation } = useMemo(() => {
    if (variant !== '25') {
      return {
        displayText: text,
        needsTruncation:
          !!fullText &&
          fullText.trim().length > 0 &&
          fullText.trim() !== text.trim(),
      };
    }

    if (text.length <= maxLength) {
      return { displayText: text, needsTruncation: false };
    }

    // Find the last space before maxLength to avoid cutting words
    const truncateAt = text.lastIndexOf(' ', maxLength);
    const cutPoint = truncateAt > 0 ? truncateAt : maxLength;

    return {
      displayText: `${text.slice(0, cutPoint)}...`,
      needsTruncation: true,
    };
  }, [variant, text, fullText, maxLength]);

  return (
    <>
      <div className={`${font} text-base text-primary/80`}>
        <span>{displayText}</span>
        {needsTruncation && (
          <>
            {' '}
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className={`inline-block ${font} text-primary underline transition-colors hover:text-primary/70`}
            >
              ver más
            </button>
          </>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex flex-col items-center gap-4">
              <DialogTitle
                className={`font-bold ${font} text-3xl text-primary sm:text-4xl`}
              >
                {projectName}
              </DialogTitle>
              {projectLogoUrl && !isDefaultLogoValue ? (
                <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border-2 border-primary/20 bg-primary/5">
                  <img
                    src={projectLogoUrl}
                    alt={projectName}
                    width={96}
                    height={96}
                    className="h-full w-full object-contain p-2"
                  />
                </div>
              ) : (
                <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-lg border-2 border-primary/20 bg-primary/10">
                  <span className={`font-bold ${font} text-4xl text-primary`}>
                    {getInitials(projectName)}
                  </span>
                </div>
              )}
              <div className="flex flex-col items-center gap-2 text-center">
                <p className={`${font} text-base text-primary/80`}>
                  {variant === '25' ? text : fullText || text}
                </p>
                {projectTrackName && (
                  <span
                    className={`inline-block w-fit bg-primary/10 px-3 py-1 ${font} text-primary text-sm`}
                  >
                    {projectTrackName}
                  </span>
                )}
              </div>
            </div>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}
